import React, { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TaskSection } from '../interfaces/Task';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import { useTranslation } from 'react-i18next';

interface SelectedItem {
    id: string;
    type: 'section' | 'task';
}

interface FolderIconProps {
    section: TaskSection;
    onDoubleClick: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onDeleteSelected?: () => void;
    onDrop: (itemId: string, itemType: 'section' | 'task') => void;
    onDropMultiple?: (items: SelectedItem[]) => void;
    onCreateSubfolder?: () => void;
    viewMode?: 'icons' | 'list';
    isSelected?: boolean;
    onSelect?: (ctrlKey: boolean) => void;
    selectedItems?: SelectedItem[];
}

export default function FolderIcon(props: FolderIconProps) {
    const { t } = useTranslation();
    const {
        section,
        onDoubleClick,
        onEdit,
        onDelete,
        onDeleteSelected,
        onDrop,
        onDropMultiple,
        onCreateSubfolder,
        viewMode = 'icons',
        isSelected = false,
        onSelect,
        selectedItems = []
    } = props;
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    // Check if this folder is part of the selection being dragged
    const isInSelection = selectedItems.some(item => item.id === section._id && item.type === 'section');
    const hasMultiSelection = selectedItems.length > 1;

    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'section',
        item: () => {
            // If this item is selected and there are multiple selections, drag all
            if (isSelected && selectedItems.length > 1) {
                return {
                    id: section._id,
                    sectionId: section._id,
                    type: 'section' as const,
                    section: section,
                    selectedItems: selectedItems
                };
            }
            return {
                id: section._id,
                sectionId: section._id,
                type: 'section' as const,
                section: section,
                selectedItems: [{ id: section._id, type: 'section' as const }]
            };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [section._id, isSelected, selectedItems]);

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ['section', 'task'],
        drop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; taskId?: string; selectedItems?: SelectedItem[] }, monitor) => {
            if (monitor.didDrop()) return;

            // Check if dropping multiple items
            if (item.selectedItems && item.selectedItems.length > 1 && onDropMultiple) {
                // Filter out this folder from the items being dropped
                const itemsToMove = item.selectedItems.filter(si => !(si.id === section._id && si.type === 'section'));
                if (itemsToMove.length > 0) {
                    onDropMultiple(itemsToMove);
                }
            } else {
                const itemId = item.type === 'section' ? item.sectionId || item.id : item.taskId || item.id;
                if (itemId) {
                    onDrop(itemId, item.type);
                }
            }
        },
        canDrop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; selectedItems?: SelectedItem[] }) => {
            // Check if trying to drop into itself
            if (item.type === 'section') {
                const sectionId = item.sectionId || item.id;
                if (sectionId === section._id) return false;
            }
            // Check if this folder is part of the selection being dragged
            if (item.selectedItems) {
                const isDropTargetInSelection = item.selectedItems.some(
                    si => si.id === section._id && si.type === 'section'
                );
                if (isDropTargetInSelection) return false;
            }
            return true;
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [section._id, onDrop, onDropMultiple]);

    // Combine drag and drop refs
    const combinedRef = (node: HTMLDivElement | null) => {
        drag(node);
        drop(node);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onSelect) {
            onSelect(e.ctrlKey || e.metaKey);
        }
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    // Context menu items depend on whether we have multi-selection
    const contextMenuItems: ContextMenuItem[] = hasMultiSelection && isSelected
        ? [
            {
                label: `${t("Delete")} (${selectedItems.length})`,
                icon: 'bi-trash',
                onClick: onDeleteSelected || onDelete,
                danger: true
            }
        ]
        : [
            {
                label: t("Open"),
                icon: 'bi-folder2-open',
                onClick: onDoubleClick
            },
            {
                label: t("Edit"),
                icon: 'bi-pencil',
                onClick: onEdit
            },
            ...(onCreateSubfolder ? [{
                label: t("New subfolder"),
                icon: 'bi-folder-plus',
                onClick: onCreateSubfolder,
                divider: true
            }] : []),
            {
                label: t("Delete"),
                icon: 'bi-trash',
                onClick: onDelete,
                danger: true,
                divider: !onCreateSubfolder
            }
        ];

    if (viewMode === 'list') {
        return (
            <>
                <div
                    ref={combinedRef}
                    className={`list-item folder-list-item ${isDragging ? 'dragging' : ''} ${isOver && canDrop ? 'drop-target' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={handleClick}
                    onDoubleClick={onDoubleClick}
                    onContextMenu={handleContextMenu}
                >
                    <div className="list-item-icon">
                        <i className="bi bi-folder-fill"></i>
                    </div>
                    <div className="list-item-name">{section.name}</div>
                    <div className="list-item-actions">
                        <button
                            className="list-action-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit();
                            }}
                            title={t("Edit")}
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                        <button
                            className="list-action-btn delete-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            title={t("Delete")}
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
                {contextMenu && (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        items={contextMenuItems}
                        onClose={() => setContextMenu(null)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div
                ref={combinedRef}
                className={`icon-item folder-icon-item ${isDragging ? 'dragging' : ''} ${isOver && canDrop ? 'drop-target' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={handleClick}
                onDoubleClick={onDoubleClick}
                onContextMenu={handleContextMenu}
                title={section.name}
            >
                <div className="icon">
                    <i className="bi bi-folder-fill"></i>
                </div>
                <div className="name">{section.name}</div>
                <div className="icon-actions">
                    <button
                        className="icon-action-btn edit-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit();
                        }}
                        title={t("Edit")}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                    <button
                        className="icon-action-btn delete-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        title={t("Delete")}
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            </div>
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenuItems}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </>
    );
}
